/**
 * Module 36 (WP-36.11) — who gets the Block Jam dev timing overlay.
 *
 * The whole point of the overlay is that WP-36.10's numbers were measured in
 * Node on a desktop while the bug was reported on an Android device. So
 * `import.meta.env.DEV` alone is not enough: an Android build made with
 * `pnpm run cap:sync` is a PRODUCTION Vite build, where that flag is false and
 * the overlay could never appear on the device it exists to measure.
 *
 * Hence three ways in, in priority order:
 *
 *  1. `import.meta.env.DEV` — the dev server, always on.
 *  2. `?perf=1` / `?perf=0` in the URL — a preview or LAN build, and the switch
 *     is **persisted**, because a Capacitor WebView loads a fixed URL and cannot
 *     be handed a query string afterwards. Enable it once (over `vite preview`
 *     on the LAN, or through `chrome://inspect`), and the Android build keeps it.
 *  3. The persisted flag on its own.
 *
 * **This means a few hundred bytes of diagnostic ship in production.** That is a
 * deliberate trade, recorded here rather than hidden: it is read-only (no
 * cheats, no state change, no analytics), off unless explicitly switched on, and
 * it is the only thing that makes an on-device performance pass possible without
 * a separate build. Delete this module and the overlay's constructor argument to
 * back it out.
 */

/** localStorage key holding the persisted switch. */
export const PERF_OVERLAY_STORAGE_KEY = 'pm.blockjam.perfOverlay';

/** The query parameter that flips it, on any build. */
export const PERF_OVERLAY_PARAM = 'perf';

export interface PerfOverlayEnvironment {
  /** `import.meta.env.DEV` at the call site. */
  isDev: boolean;
  /** `location.search`, or an empty string where there is no URL. */
  search: string;
  /** `localStorage`, or null when it is unavailable (Safari private mode). */
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

/**
 * Decide whether to show the overlay, applying (and persisting) any explicit
 * `?perf=` instruction. Pure apart from the storage write, which is the point of
 * the parameter — a query string that did not persist would be useless to the
 * Android pass.
 */
export function resolvePerfOverlayFlag(env: PerfOverlayEnvironment): boolean {
  const requested = readParam(env.search);

  if (requested !== null) {
    try {
      if (requested) env.storage?.setItem(PERF_OVERLAY_STORAGE_KEY, '1');
      else env.storage?.removeItem(PERF_OVERLAY_STORAGE_KEY);
    } catch {
      // A storage quota/permission failure must never stop the app booting; the
      // flag simply does not survive this session.
    }
    // An explicit `?perf=0` turns it off even on the dev server — otherwise
    // there would be no way to measure the app WITHOUT the measuring overlay,
    // which is exactly when a frame-time reading is worth having.
    return requested;
  }

  if (env.isDev) return true;

  try {
    return env.storage?.getItem(PERF_OVERLAY_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/** `true`/`false` from `?perf=`, or null when it was not given at all. */
function readParam(search: string): boolean | null {
  if (!search) return null;
  let value: string | null;
  try {
    value = new URLSearchParams(search).get(PERF_OVERLAY_PARAM);
  } catch {
    return null;
  }
  if (value === null) return null;
  // A bare `?perf` (empty value) reads as "on" — it is what anyone typing it
  // means, and requiring `=1` would just be a trap.
  return value !== '0' && value.toLowerCase() !== 'false';
}

/** The flag for the running app. */
export function currentPerfOverlayFlag(): boolean {
  return resolvePerfOverlayFlag({
    isDev: import.meta.env.DEV,
    search: typeof location === 'undefined' ? '' : location.search,
    storage: safeStorage(),
  });
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
